import React, { useEffect, useState } from "react";

import { useLocation, useNavigate, useParams } from "react-router-dom";

import {
  Alert,
  Badge,
  Button,
  Caption,
  Checkbox,
  CopyButton,
  DetailedSubheading,
  Flex,
  InputSearch,
  Tab,
  TabsMenu,
  TabsProvider,
  Tooltip,
} from "@olist/design-system";
import { DataTable, PageList } from "@olist/templates";

import { useGetTickets } from "./hooks/useGetTickets";
import {
  createFilterString,
  productDictionary,
  productsFilterCheckboxes,
  productsFiltersInitialState,
  statutsFiltersDictionary,
} from "./settings";
import {
  PageDescription,
  SupportTicketsWrapper,
  TicketDescription,
  TruncatedText,
} from "./styles";
import { Cactus, InternalServerError } from "../../icons";
import { ErrorHandling } from "components/errorHandling";
import { formatDate } from "helpers/dateUtils";
import { useCustomNavigate } from "utils";

export function SupportTickets() {
  const [currentTab, setCurrentTab] = useState("todos");
  const [search, setSearch] = useState("");
  const [searchText, setSearchText] = useState<string | undefined>("");

  const navigate = useCustomNavigate();
  const navigator = useNavigate();
  const location = useLocation();
  const params = useParams();

  const [filters, setFilters] = useState({
    ...productsFiltersInitialState,
    ...(params.product
      ? {
          [params.product as string]: true,
        }
      : {}),
  });
  const [filterString, setFilterString] = useState(
    params?.product ? createFilterString(filters) : ""
  );
  const [currentPage, setCurrentPage] = useState(1);

  const { isError, isLoading, tickets, totalPages } = useGetTickets({
    currentPage,
    products: filterString,
    status: currentTab !== "todos" ? currentTab : undefined,
    search: searchText,
  });

  const handleClean = () => {
    if (params.product) {
      navigate("/support");
    }

    setFilters(productsFiltersInitialState);
    setFilterString("");
  };

  const handleProductFilterChange = (
    product: keyof typeof productsFiltersInitialState
  ) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [product]: !prevFilters[product],
    }));
  };

  const handleFilterTrigger = () => {
    const selectedFilters = createFilterString(filters);
    setFilterString(selectedFilters);
    setCurrentPage(1);
  };

  const handleStatus = (value: string) => {
    setCurrentTab(value);
    setCurrentPage(1);
  };

  const handleSearch = () => {
    setSearchText(search);
  };

  const handleSearchClean = () => {
    setSearchText(undefined);
    setSearch("");
  };

  const handleOpenNewTicket = () => {
    navigator({
      pathname: "/support/new-ticket/erp",
      search: location.search,
    });
  };

  useEffect(() => {
    document.title = "Minhas solicitações - Central de ajuda";
    if (location.search.includes("form-tiny")) {
      handleOpenNewTicket();
    }
    if (location.search.includes("case-")) {
      const caseTicket = location.search.split("case-").at(-1);
      navigator({
        pathname: `/support/ticket/${caseTicket}`,
        search: location.search,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rows = tickets.map((ticket) => {
    const product = Object.keys(productDictionary).includes(ticket.product)
      ? productDictionary[ticket.product]
      : ticket.product;

    return {
      key: ticket.id,
      data: [
        {
          key: 0,
          value: (
            <Flex gap="inline.3xsmall" alignItems="center">
              <Caption>#{ticket.number}</Caption>
              <CopyButton valueToCopy={ticket.number} />
            </Flex>
          ),
        },
        {
          key: 1,
          value: (
            <Badge
              variation={
                statutsFiltersDictionary[ticket.status.toLocaleLowerCase()]
                  .variation
              }
              showIcon={false}
            >
              {
                statutsFiltersDictionary[ticket.status.toLocaleLowerCase()]
                  .label
              }
            </Badge>
          ),
        },
        { key: 2, value: product },
        {
          key: 3,
          value: (
            <Flex flexDirection="column" width="400px">
              <TruncatedText>{ticket.title}</TruncatedText>
              <Tooltip content={ticket.description}>
                <TicketDescription color="neutral.base">
                  {ticket.description}
                </TicketDescription>
              </Tooltip>
            </Flex>
          ),
        },
        { key: 4, value: formatDate(ticket.created_at) },
        { key: 5, value: formatDate(ticket.updated_at) },
        {
          key: 6,
          value: (
            <Button
              variation="secondary"
              onClick={() => navigate(`/support/ticket/${ticket.id}`)}
            >
              ver mais
            </Button>
          ),
        },
      ],
    };
  });

  const shouldShowHeader = !isLoading && !isError;
  const shouldShowClearFilter = Boolean(filterString);

  return (
    <SupportTicketsWrapper>
      <Alert
        showIcon
        title="Atenção:"
        variation="alert"
        description="Os chamados de ERP anteriores ao dia 02/09/2025, deverão ser acompanhados e respondidos pelo seu e-mail. Em caso de dúvidas, contate o atendimento."
      />
      <PageList
        title="Meus chamados"
        description={
          <Flex
            flexDirection="column"
            gap="stack.2xsmall"
            marginBottom="stack.medium"
          >
            <PageDescription>
              Aqui você pode consultar os chamados que criou com a gente. Todas
              as comunicações, incluindo as suas respostas e as nossas, serão
              feitas pelo seu e-mail cadastrado.
            </PageDescription>
            <TabsProvider
              currentTab={currentTab}
              onChange={(tabKey) => handleStatus(tabKey as string)}
            >
              <TabsMenu>
                <Tab tabKey="todos">Todos</Tab>
                <Tab tabKey="Aberto">Abertos</Tab>
                <Tab tabKey="Pendente">Aguardando sua resposta</Tab>
                <Tab tabKey="Fechado">Finalizados</Tab>
              </TabsMenu>
            </TabsProvider>
          </Flex>
        }
        isError={isError}
        isErrorComponent={
          <ErrorHandling
            image={<InternalServerError />}
            title="Algo inesperado aconteceu"
            subtitle="Ocorreu um erro inesperado, mas fomos notificados!"
          />
        }
        isLoading={isLoading}
        isEmpty={!isLoading && tickets.length === 0}
        isEmptyComponent={
          <ErrorHandling
            image={<Cactus />}
            title="Nenhuma solicitação encontrada"
            subtitle="Não há solicitações de ajuda para exibir para você no momento"
          />
        }
        table={{
          columns: [
            { key: 0, name: "Número do chamado" },
            { key: 1, name: "Status" },
            { key: 2, name: "Produto" },
            { key: 3, name: "Assunto" },
            { key: 4, name: "Data de criação" },
            { key: 5, name: "Última atualização" },
            { key: 6, name: "" },
          ],
          rows,
        }}
        pagination={{
          currentPage,
          pageCount: totalPages,
          handlePaginationChange: (page: number) => setCurrentPage(page),
        }}
        header={
          shouldShowHeader ? (
            <DataTable.TableHeader justifyContent="flex-end" gap="inline.small">
              {shouldShowClearFilter ? (
                <Flex>
                  <Button variation="secondary" onClick={handleClean}>
                    limpar filtros
                  </Button>
                </Flex>
              ) : (
                <></>
              )}
              <DataTable.Filter
                onFilter={handleFilterTrigger}
                onClean={handleClean}
                triggerVariation="secondary"
              >
                <Flex width="100%" flexDirection="column" gap="stack.medium">
                  <DetailedSubheading mb={8}>Produto:</DetailedSubheading>
                  <Flex gap="inline.medium" flexWrap="wrap">
                    {productsFilterCheckboxes.map(({ key, label }) => (
                      <Checkbox
                        key={key}
                        label={label}
                        checked={filters[key]}
                        onChange={() => handleProductFilterChange(key)}
                      />
                    ))}
                  </Flex>
                </Flex>
              </DataTable.Filter>
              <Flex width="32%">
                <InputSearch
                  value={search}
                  onClean={handleSearchClean}
                  onSearch={handleSearch}
                  onChange={({ target }) => setSearch(target.value)}
                  placeholder="Buscar por número do chamado ou assunto"
                  debounceDelay={1200}
                />
              </Flex>
            </DataTable.TableHeader>
          ) : null
        }
      />
    </SupportTicketsWrapper>
  );
}
